import styled from '@emotion/styled';
import { breakpoint_small, breakpoint_large, breakpoint_macbook_small, breakpoint_macbook, breakpoint_macbook_large } from '../../../../../shared/constants/breakpoints';
import {
    color_shades_light,
    color_shades_lighter,
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_colors_ocean,
    color_defaults_white,
    voicify_spark_darkest,
    voicify_spark_darker,
    voicify_spark_dark,
    voicify_spark_medium,
    voicify_spark_light,
    voicify_spark_lightest,
    dark_grey_blue,
    chart_planned_fill_light,
    chart_planned_solid,
    chart_unplanned_fill_light,
    chart_unplanned_solid,
    button_dark_bg,
    button_dark_bg_hover,
    button_shadow_light,
    shadow_sm,
    shadow_md,
    shadow_md_alt,
    shadow_lg,
    shadow_xl,
    shadow_modal,
    overlay_white,
    premium_gold,
    premium_gold_gradient,
    premium_gold_shadow,
    premium_gold_shadow_hover,
    status_success,
    status_success_bg,
    status_error,
    status_error_bg,
    status_error_bg_light,
    status_error_bg_xlight,
    status_neutral,
    status_neutral_bg,
    dark_grey_blue_70,
    dark_grey_blue_80,
    chart_glass_blue_4,
    chart_blue_4,
    chart_slate_glass,
    chart_slate_2
} from '../../../../../shared/constants/colors';
import { style_border_default } from '../../../../../shared/constants/stylesValues';
import { baseTileCSS, tileSizeConfig } from '../../../../../shared/constants/chartTheme';
import SecondaryLoader from '../../../../../shared/components/general/SecondaryLoader';

const crownIcon = require('../../../../../shared/content/images/analytics-icons/crown.svg');

// ==================== UTILITY STYLED COMPONENTS ====================

export const HideOnMobile = styled.span`
    ${breakpoint_small} {
        display: none !important;
    }
`;

// ==================== LAYOUT STYLED COMPONENTS ====================

export const DashboardWrapper = styled.div`
    background-color: ${color_shades_lighter};
    height: calc(100vh - 80px);
    display: flex;
    overflow: hidden;
    
    /* Mac font smoothing for better text rendering on Retina displays */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    
    ${breakpoint_small} {
        flex-direction: column;
        height: auto;
        overflow: visible;
    }
`;

export const LeftSidePanel = styled.div<{ isCollapsed?: boolean }>`
    width: ${props => props.isCollapsed ? '70px' : '380px'};
    background-color: ${color_defaults_white};
    display: flex;
    flex-direction: column;
    overflow: visible;
    border-right: 1px solid ${color_shades_light};
    position: relative;
    transition: width 0.3s ease;

    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        width: ${props => props.isCollapsed ? '70px' : '330px'};
    }

    /* Mac 13" at default/tight scaling - narrower panel */
    ${breakpoint_macbook_small} {
        width: ${props => props.isCollapsed ? '70px' : '290px'};
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        width: ${props => props.isCollapsed ? '60px' : '260px'};
    }
    
    ${breakpoint_small} {
        width: 100%;
        min-height: ${props => props.isCollapsed ? '70px' : '400px'};
        border-right: none;
        border-bottom: 1px solid ${color_shades_light};
    }
`;

export const CollapsedSidebarContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 0;
    height: 100%;
`;

export const CollapsedAvatarWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    cursor: pointer;
    margin-bottom: 16px;
    
    &:hover {
        transform: scale(1.05);
    }
    
    transition: transform 0.2s ease;
`;

export const CollapsedAvatarRing = styled.div`
    position: absolute;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${voicify_spark_lightest};
    opacity: 0.6;
`;

export const ExpandSidebarButton = styled.button`
    background: ${voicify_spark_lightest};
    border: 1px solid ${voicify_spark_light};
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: auto;
    margin-bottom: 16px;
    
    &:hover {
        background: ${voicify_spark_light};
        border-color: ${voicify_spark_medium};
    }
`;

export const ExpandArrowIcon = styled.svg`
    width: 20px;
    height: 20px;
    fill: ${voicify_spark_darker};
`;

export const CollapseSidebarButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    background: ${color_shades_lighter};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${voicify_spark_lightest};
        border-color: ${voicify_spark_light};
    }
`;

export const CollapseArrowIcon = styled.svg`
    width: 18px;
    height: 18px;
    fill: ${color_shades_darker};
`;

// ==================== COLLAPSED HEADER STYLED COMPONENTS ====================

export const CollapsedDateRangeText = styled.span`
    position: absolute;
    top: 16px;
    left: 48px;
    font-size: 14px;
    color: #6b7280;
    font-weight: 700;
    z-index: 100;
    
    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        left: 40px;
        font-size: 12px;
    }
    
    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        left: 44px;
        font-size: 13px;
    }
    
    /* Mac 14" / external displays */
    ${breakpoint_macbook_large} {
        left: 46px;
    }

    ${breakpoint_small} {
        display: none;
    }
`;

// ==================== SHARE BUTTON STYLED COMPONENTS ====================

export const ShareButtonWrapper = styled.div`
    position: absolute;
    top: 10px;
    right: 48px;
    z-index: 100;
    display: flex;
    align-items: center;
    gap: 16px;
    
    /* Mac 13" at default/tight scaling - match MainContent padding */
    ${breakpoint_macbook_small} {
        right: 40px;
    }
    
    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        right: 44px;
    }
    
    /* Mac 14" / external displays */
    ${breakpoint_macbook_large} {
        right: 46px;
    }

    ${breakpoint_small} {
        display: none;
    }
`;

export const ShareButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: ${shadow_sm};
    transition: all 0.2s ease;
    
    &:hover {
        background: ${voicify_spark_dark};
        box-shadow: ${shadow_md};
    }
    
    &:active {
        transform: scale(0.98);
    }
`;

export const ShareIcon = styled.svg`
    width: 14px;
    height: 14px;
    fill: currentColor;
`;

export const ShareDropdownArrow = styled.span`
    font-size: 8px;
    margin-left: 2px;
    opacity: 0.8;
`;

export const ShareDropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    box-shadow: ${shadow_modal};
    min-width: 160px;
    overflow: hidden;
    z-index: 101;
`;

export const ShareDropdownItem = styled.button<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 14px;
    background: transparent;
    border: none;
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.disabled ? color_shades_dark : color_shades_darkest};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.15s ease;
    text-align: left;
    
    &:hover {
        background: ${props => props.disabled ? 'transparent' : voicify_spark_lightest};
    }
    
    &:not(:last-child) {
        border-bottom: 1px solid ${color_shades_lighter};
    }
`;

export const ShareDropdownIcon = styled.svg`
    width: 16px;
    height: 16px;
    fill: ${voicify_spark_darker};
    flex-shrink: 0;
`;

export const BottomGraphic = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, ${color_defaults_white} 0%, ${voicify_spark_lightest} 40%, transparent 100%);
    border-radius: 0 100% 0 0;
    opacity: 1;
    pointer-events: none;
    z-index: 0;
`;

// ==================== AVATAR & ORG STYLED COMPONENTS ====================

export const OrgAvatarSection = styled.div<{ $variant?: 'food-ordering' | 'healthcare' | 'default' }>`
    padding: 40px 24px 0px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    overflow: hidden;
    
    /* Mac 13" - reduced padding */
    ${breakpoint_macbook_small} {
        padding: 16px 12px 0 12px;
    }
    
    ${breakpoint_macbook} {
        padding: 32px 20px 0 20px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        padding: 8px 8px 0 8px;
    }
    
    background: ${props => {
        if (props.$variant === 'food-ordering') {
            return `linear-gradient(165deg, 
                rgba(46, 184, 232, 0.08) 0%, 
                rgba(68, 190, 236, 0.04) 30%,
                ${color_defaults_white} 60%)`;
        } else if (props.$variant === 'healthcare') {
            return `linear-gradient(165deg, 
                rgba(100, 116, 139, 0.10) 0%, 
                rgba(71, 85, 105, 0.05) 30%,
                ${color_defaults_white} 60%)`;
        }
        return color_defaults_white;
    }};
`;

export const VerticalAccentDecoration = styled.div<{ $variant?: 'food-ordering' | 'healthcare' | 'default' }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    opacity: ${props => props.$variant === 'default' ? 0 : 1};
    transition: opacity 0.3s ease;
    
    background: ${props => {
        if (props.$variant === 'food-ordering') {
            return `linear-gradient(90deg, 
                ${chart_blue_4} 0%, 
                ${chart_glass_blue_4} 50%,
                transparent 100%)`;
        } else if (props.$variant === 'healthcare') {
            return `linear-gradient(90deg, 
                ${chart_slate_2} 0%, 
                ${chart_slate_glass} 50%,
                transparent 100%)`;
        }
        return 'transparent';
    }};
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 60%;
        height: 100%;
        filter: blur(8px);
        opacity: 0.6;
        background: ${props => {
        if (props.$variant === 'food-ordering') {
            return chart_blue_4;
        } else if (props.$variant === 'healthcare') {
            return chart_slate_2;
        }
        return 'transparent';
    }};
    }
`;

export const AvatarWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 215px;
    height: 215px;
    
    /* Mac 13" - smaller avatar */
    ${breakpoint_macbook_small} {
        width: 165px;
        height: 165px;
        
        &::before {
            width: 135px;
            height: 135px;
        }
    }
    
    ${breakpoint_macbook} {
        width: 180px;
        height: 180px;
        
        &::before {
            width: 150px;
            height: 150px;
        }
    }
    
    &::before {
        content: '';
        position: absolute;
        width: 175px;
        height: 175px;
        border-radius: 50%;
        background: ${color_defaults_white};
        z-index: 1;
    }
    
    > *:not(:first-child):not(:nth-child(2)) {
        z-index: 2;
    }
`;

export const OrgName = styled.h2`
    color: ${dark_grey_blue};
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 24px 0;
    text-align: center;
    padding: 0 24px;
    
    /* Mac 13" - smaller text */
    ${breakpoint_macbook_small} {
        font-size: 16px;
        margin: 0 0 10px 0;
        padding: 0 8px;
    }
    
    ${breakpoint_macbook} {
        font-size: 20px;
        margin: 0 0 20px 0;
        padding: 0 20px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 13px;
        margin: 0 0 8px 0;
        padding: 0 6px;
    }
`;

/* Scales down the org avatar at small laptop sizes using CSS zoom so the
   JS-calculated pixel dimensions inside OrganizationAvatarUpload shrink
   without needing prop changes. */
export const AvatarScaleWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoint_macbook_small} {
        zoom: 0.72;
    }

    ${breakpoint_large} {
        zoom: 0.56;
    }
`;

// ==================== BADGE STYLED COMPONENTS ====================

export const PlanBadge = styled.div`
    position: absolute;
    top: 195px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 18px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    box-shadow: ${shadow_md};
    z-index: 10;
    backdrop-filter: blur(4px);
    
    /* Mac 13" - adjust position for smaller avatar */
    ${breakpoint_macbook_small} {
        top: 145px;
        padding: 5px 14px;
        font-size: 10px;
    }
    
    ${breakpoint_macbook} {
        top: 160px;
        padding: 5px 16px;
    }
`;

export const StarterBadge = styled(PlanBadge)`
    background-color: ${voicify_spark_darkest};
    color: ${color_defaults_white};
`;

export const StarterBadgeButton = styled.button`
    position: absolute;
    top: 195px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 18px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    box-shadow: ${shadow_md};
    z-index: 10;
    backdrop-filter: blur(4px);
    background-color: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    
    /* Mac 13" - adjust position for smaller avatar */
    ${breakpoint_macbook_small} {
        top: 145px;
        padding: 5px 14px;
        font-size: 10px;
    }
    
    ${breakpoint_macbook} {
        top: 160px;
        padding: 5px 16px;
    }

    &:hover {
        background-color: ${voicify_spark_dark};
        box-shadow: ${shadow_lg};
        transform: translateX(-50%) scale(1.02);
    }

    &:active {
        transform: translateX(-50%) scale(0.98);
    }
`;

export const PremiumBadge = styled(PlanBadge)`
    background: ${premium_gold_gradient};
    color: ${color_defaults_white};
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px 6px 12px;
    box-shadow: ${premium_gold_shadow};
    border: 1px solid ${premium_gold};
    
    &:before {
        content: '';
        width: 14px;
        height: 14px;
        background-image: url(${crownIcon});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        display: inline-block;
        filter: brightness(0) invert(1);
    }
`;

// ==================== UPGRADE MODAL STYLED COMPONENTS ====================

export const UpgradeModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: ${dark_grey_blue_70};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

export const UpgradeModalContent = styled.div`
    background: ${color_defaults_white};
    border-radius: 20px;
    border: 12px solid ${dark_grey_blue_80};
    max-width: 520px;
    width: 90%;
    overflow: hidden;
    position: relative;
`;

export const UpgradeModalCloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 16px;
    background: none;
    border: none;
    font-size: 28px;
    color: ${color_shades_dark};
    cursor: pointer;
    padding: 0;
    line-height: 1;
    z-index: 1;
    
    &:hover {
        color: ${color_shades_darkest};
    }
`;

export const UpgradeModalBody = styled.div`
    padding: 32px 32px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const PlanSection = styled.div`
    width: 100%;
    text-align: center;
`;

export const PlanLabel = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${color_shades_darkest};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 6px;
`;

export const PlanName = styled.div<{ premium?: boolean }>`
    font-size: 24px;
    font-weight: 700;
    color: ${props => props.premium ? premium_gold : color_shades_darkest};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 12px;
`;

export const PlanTag = styled.span<{ premium?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${props => props.premium
        ? premium_gold_gradient
        : voicify_spark_lightest};
    color: ${props => props.premium ? color_shades_darkest : voicify_spark_darker};
    border: 1px solid ${props => props.premium ? premium_gold : voicify_spark_light};
`;

export const PlanDescription = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    line-height: 1.6;
    margin: 0;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
`;

export const UpgradeDivider = styled.div`
    width: 100%;
    height: 1px;
    background: ${color_shades_lighter};
    margin: 24px 0;
`;

export const UpgradeModalButton = styled.button`
    margin-top: 8px;
    background: ${premium_gold_gradient};
    color: ${color_shades_darkest};
    border: 1px solid ${premium_gold};
    padding: 12px 32px;
    border-radius: 40px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: ${premium_gold_shadow};
    transition: all 0.2s ease;

    &:hover {
        box-shadow: ${premium_gold_shadow_hover};
        transform: scale(1.02);
    }

    &:active {
        transform: scale(0.98);
    }
`;

// ==================== SIDEBAR STYLED COMPONENTS ====================

export const DatePickerSection = styled.div`
    padding: 16px 16px;
    position: relative;
    background-color: ${color_defaults_white};
    z-index: 100;

    /* Mac 13" at More Space scaling */
    ${breakpoint_macbook} {
        padding: 14px 14px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        padding: 12px 10px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        padding: 10px 8px;
    }
`;

export const LocationsSection = styled.div<{ $isCollapsed?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 20px;
    position: relative;
    z-index: 2;

    /* Mac 13" at More Space scaling */
    ${breakpoint_macbook} {
        padding: 16px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        padding: 12px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        padding: 10px;
    }
    
    ${breakpoint_small} {
        flex: ${props => props.$isCollapsed ? 'none' : '1'};
        min-height: ${props => props.$isCollapsed ? 'auto' : '200px'};
    }
`;

export const LocationsHeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    ${breakpoint_macbook_small} {
        margin-bottom: 12px;
    }

    ${breakpoint_large} {
        margin-bottom: 10px;
    }
`;

export const MobileAssistantToggle = styled.button<{ $isCollapsed?: boolean }>`
    display: none;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    background: ${voicify_spark_lightest};
    border: 1px solid ${voicify_spark_light};
    border-radius: 8px;
    cursor: pointer;
    margin-bottom: ${props => props.$isCollapsed ? '0' : '12px'};
    transition: all 0.2s ease;

    &:hover {
        background: ${voicify_spark_light};
        border-color: ${voicify_spark_medium};
    }

    ${breakpoint_small} {
        display: flex;
    }
`;

export const MobileToggleText = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${voicify_spark_darker};
`;

export const MobileToggleIcon = styled.svg<{ $isCollapsed?: boolean }>`
    width: 20px;
    height: 20px;
    fill: ${voicify_spark_darker};
    transition: transform 0.2s ease;
    transform: ${props => props.$isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
`;

export const CollapsibleContent = styled.div<{ $isCollapsed?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    ${breakpoint_small} {
        display: ${props => props.$isCollapsed ? 'none' : 'flex'};
        max-height: ${props => props.$isCollapsed ? '0' : '400px'};
        overflow: hidden;
        transition: max-height 0.3s ease;
    }
`;

export const LocationsTitle = styled.h3`
    color: ${color_shades_darker};
    font-size: 11px;
    font-weight: 600;
    margin: 0;
    letter-spacing: 0.8px;
    text-transform: uppercase;

    ${breakpoint_macbook_small} {
        font-size: 9px;
        letter-spacing: 0.6px;
    }

    ${breakpoint_large} {
        font-size: 8px;
        letter-spacing: 0.5px;
    }
`;

export const SelectionControls = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const SelectionButton = styled.button<{ $isDisabled?: boolean }>`
    background: none;
    border: none;
    color: ${props => props.$isDisabled ? color_shades_dark : voicify_spark_darker};
    font-size: 10px;
    font-weight: 600;
    cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
    padding: 2px 4px;
    border-radius: 4px;
    transition: all 0.2s;
    opacity: ${props => props.$isDisabled ? 0.5 : 1};

    &:hover {
        background-color: ${props => props.$isDisabled ? 'transparent' : voicify_spark_lightest};
        color: ${props => props.$isDisabled ? color_shades_dark : voicify_spark_dark};
    }

    ${breakpoint_macbook_small} {
        font-size: 8px;
        padding: 2px 3px;
    }

    ${breakpoint_large} {
        font-size: 10px;
        padding: 1px 2px;
    }
`;

export const SelectionDivider = styled.span`
    color: ${color_shades_light};
    font-size: 10px;
`;

export const FilterIconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 2px;
`;

export const SpanishIndicatorBadge = styled.span<{ $isDisabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${voicify_spark_darker};
    color: white;
    font-size: 8px;
    font-weight: 700;
    padding: 2px 4px;
    border-radius: 3px;
    letter-spacing: 0.5px;
    opacity: ${props => props.$isDisabled ? 0.5 : 1};
    cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
    transition: transform 0.2s, background 0.2s;
    
    &:hover {
        transform: ${props => props.$isDisabled ? 'none' : 'scale(1.05)'};
        background: ${props => props.$isDisabled ? voicify_spark_darker : voicify_spark_darkest};
    }
`;

export const FilterIconButton = styled.button<{ isActive: boolean; $isDisabled?: boolean }>`
    background: none;
    border: none;
    cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
    opacity: ${props => props.$isDisabled ? 0.5 : 1};

    &:hover {
        background-color: ${props => props.$isDisabled ? 'transparent' : voicify_spark_lightest};
    }
`;

export const FilterIconSvg = styled.svg<{ isActive: boolean; $isDisabled?: boolean }>`
    width: 14px;
    height: 14px;
    fill: ${props => props.$isDisabled ? color_shades_dark : (props.isActive ? voicify_spark_darker : color_shades_darker)};
    transition: fill 0.2s;
`;

export const FilterDropdown = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background-color: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    box-shadow: ${shadow_md_alt};
    min-width: 100px;
    z-index: 100;
    overflow: hidden;
`;

export const FilterDropdownItem = styled.button<{ isActive: boolean }>`
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background-color: ${props => props.isActive ? voicify_spark_lightest : 'transparent'};
    color: ${props => props.isActive ? voicify_spark_darker : color_shades_darker};
    font-size: 11px;
    font-weight: ${props => props.isActive ? 600 : 500};
    text-align: left;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: ${voicify_spark_lightest};
    }

    ${breakpoint_macbook_small} {
        font-size: 10px;
        padding: 6px 10px;
    }

    ${breakpoint_large} {
        font-size: 9px;
        padding: 5px 8px;
    }
`;

export const AssistantSearchWrapper = styled.div<{ $isDisabled?: boolean }>`
    display: flex;
    align-items: center;
    background-color: ${props => props.$isDisabled ? '#f5f5f5' : color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 6px 10px;
    margin-bottom: 12px;
    transition: all 0.2s;
    opacity: ${props => props.$isDisabled ? 0.6 : 1};
    cursor: ${props => props.$isDisabled ? 'not-allowed' : 'auto'};

    &:focus-within {
        border-color: ${props => props.$isDisabled ? color_shades_light : voicify_spark_medium};
        box-shadow: ${props => props.$isDisabled ? 'none' : `0 0 0 2px ${voicify_spark_lightest}`};
    }

    ${breakpoint_macbook_small} {
        padding: 5px 8px;
        margin-bottom: 10px;
    }

    ${breakpoint_large} {
        padding: 4px 7px;
        margin-bottom: 8px;
    }
`;

export const AssistantSearchInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    font-size: 12px;
    color: ${color_shades_darkest};
    background: transparent;
    
    &::placeholder {
        color: ${color_shades_dark};
    }

    &:disabled {
        cursor: not-allowed;
        color: ${color_shades_dark};
    }

    ${breakpoint_macbook_small} {
        font-size: 10px;
    }

    ${breakpoint_large} {
        font-size: 9px;
    }
`;

export const AssistantSearchClear = styled.button`
    background: none;
    border: none;
    color: ${color_shades_dark};
    font-size: 16px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    transition: color 0.2s;

    &:hover {
        color: ${color_shades_darkest};
    }
`;

export const NoResultsText = styled.p`
    text-align: center;
    color: ${color_shades_darker};
    padding: 16px;
    font-size: 12px;
    font-style: italic;

    ${breakpoint_macbook_small} {
        font-size: 10px;
        padding: 12px;
    }

    ${breakpoint_large} {
        font-size: 9px;
        padding: 10px;
    }
`;

export const LocationsScrollContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding-top: 4px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: ${color_shades_dark};
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: ${color_shades_darker};
    }

    > * {
        margin-bottom: 8px;
    }

    > *:last-child {
        margin-bottom: 0;
    }
`;

export const SidebarLoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 32px 16px;
    width: 100%;
`;

// ==================== MAIN CONTENT STYLED COMPONENTS ====================

export const MainContent = styled.div<{ $isCollapsed?: boolean }>`
    flex: 1;
    padding: ${props => props.$isCollapsed ? '48px' : '24px'};
    overflow-y: auto;
    position: relative;
    transition: padding 0.3s ease;
    
    /* Mac font smoothing for better text rendering on Retina displays */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    
    /* Mac 13" at default/tight scaling - reduced padding */
    ${breakpoint_macbook_small} {
        padding: ${props => props.$isCollapsed ? '40px' : '16px'};
    }
    
    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        padding: ${props => props.$isCollapsed ? '44px' : '18px'};
    }
    
    /* Mac 14" / external displays */
    ${breakpoint_macbook_large} {
        padding: ${props => props.$isCollapsed ? '46px' : '20px'};
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        padding: ${props => props.$isCollapsed ? '40px 14px 14px 14px' : '12px 14px 8px 14px'};
    }
    
    ${breakpoint_small} {
        padding: ${props => props.$isCollapsed ? '48px 24px' : '12px'};
    }
`;

export const MainContentLoaderWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    
    ${breakpoint_small} {
        min-height: 300px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

// ==================== DASHBOARD GRID STYLED COMPONENTS ====================

export const DashboardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: minmax(0, 0.75fr) minmax(0, 0.5fr) minmax(0, 1.5fr);
    gap: 24px;
    height: 100%;
    min-height: 0;
    
    /* Mac font smoothing for Retina displays */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    
    /* Mac 13" at default/tight scaling - smaller gaps and adjusted rows */
    ${breakpoint_macbook_small} {
        gap: 14px;
        grid-template-rows: minmax(0, 0.8fr) minmax(0, 0.55fr) minmax(0, 1.4fr);
    }
    
    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        gap: 16px;
        grid-template-rows: minmax(0, 0.78fr) minmax(0, 0.52fr) minmax(0, 1.45fr);
    }
    
    /* Mac 14" / external displays */
    ${breakpoint_macbook_large} {
        gap: 18px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        gap: 10px;
        grid-template-rows: minmax(0, 0.85fr) minmax(0, 0.6fr) minmax(0, 1.35fr);
    }
    
    ${breakpoint_small} {
        grid-template-columns: 1fr;
        grid-template-rows: auto;
        gap: 12px;
        overflow: visible;
        display: flex;
        flex-direction: column;
        height: auto;

        /* Hide View & Export buttons on mobile */
        [data-view-export-button] {
            display: none !important;
        }
    }
`;

export const BaseTile = styled.div`
    ${baseTileCSS}
    
    /* Mac 13" at default/tight scaling - smaller padding */
    ${breakpoint_macbook_small} {
        padding: 12px;
        border-radius: 12px;
    }
    
    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        padding: 14px;
        border-radius: 14px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        padding: 10px;
        border-radius: 10px;
    }

    ${breakpoint_small} {
        padding: 12px;
        height: auto;
        min-height: 250px;
    }
`;

export const ChartTile = styled(BaseTile)`
    grid-column: ${tileSizeConfig.chart.gridColumn};
    grid-row: ${tileSizeConfig.chart.gridRow};
    min-height: 0;
    
    ${breakpoint_small} {
        grid-column: span 1;
        grid-row: auto;
        min-height: ${tileSizeConfig.chart.mobileMinHeight};
        flex: none;
    }
`;

export const WideChartTile = styled(BaseTile)`
    grid-column: ${tileSizeConfig.wide.gridColumn};
    grid-row: ${tileSizeConfig.wide.gridRow};
    min-height: 0;
    ${breakpoint_small} {
        grid-column: span 1;
        grid-row: auto;
        min-height: ${tileSizeConfig.wide.mobileMinHeight};
        flex: none;
    }
`;

export const NumberTile = styled(BaseTile)`
    grid-column: ${tileSizeConfig.number.gridColumn};
    grid-row: ${tileSizeConfig.number.gridRow};
    min-height: 0;
    ${breakpoint_small} {
        grid-column: span 1;
        grid-row: auto;
        min-height: ${tileSizeConfig.number.mobileMinHeight};
        flex: none;
    }
`;

export const SmallNumberTile = styled(BaseTile)`
    grid-column: ${tileSizeConfig.smallNumber.gridColumn};
    grid-row: ${tileSizeConfig.smallNumber.gridRow};
    min-height: 0;
    ${breakpoint_small} {
        grid-column: span 1;
        grid-row: auto;
        min-height: ${tileSizeConfig.smallNumber.mobileMinHeight};
        flex: none;
    }
`;

// Responsive container for 4 small number tiles in a row
export const SmallTilesRow = styled.div`
    grid-column: span 6;
    display: flex;
    gap: 24px;
    
    /* Mac 13" at default/tight scaling - smaller gaps */
    ${breakpoint_macbook_small} {
        gap: 14px;
    }
    
    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        gap: 16px;
    }
    
    /* Mac 14" / external displays */
    ${breakpoint_macbook_large} {
        gap: 18px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        gap: 10px;
    }
    
    ${breakpoint_small} {
        grid-column: span 1;
        flex-direction: column;
        gap: 12px;
        width: 100%;
    }
`;

export const TallChartTile = styled(BaseTile)`
    grid-column: ${tileSizeConfig.tall.gridColumn};
    grid-row: ${tileSizeConfig.tall.gridRow};
    min-height: 0;
    
    ${breakpoint_small} {
        grid-column: span 1;
        grid-row: auto;
        min-height: ${tileSizeConfig.tall.mobileMinHeight};
        flex: none;
    }
`;

// ==================== TILE HEADER STYLED COMPONENTS ====================

export const TileHeader = styled.div`
    margin-bottom: 4px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;

    /* Mac 13" at More Space scaling */
    ${breakpoint_macbook} {
        gap: 7px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        gap: 5px;
        margin-bottom: 3px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        gap: 4px;
        margin-bottom: 2px;
    }

    ${breakpoint_small} {
        margin-bottom: 4px;
        gap: 6px;
    }
`;

export const TileTitle = styled.h3<{ small?: boolean }>`
    font-size: ${props => props.small ? '13px' : '15px'};
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0 0 2px 0;
    letter-spacing: -0.2px;
    text-align: center;

    /* Mac 13" at "More Space" scaling - modest reduction */
    ${breakpoint_macbook} {
        font-size: ${props => props.small ? '12px' : '14px'};
    }

    /* Mac 13" at default/tight scaling - smaller screens */
    ${breakpoint_macbook_small} {
        font-size: ${props => props.small ? '9px' : '11px'};
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: ${props => props.small ? '8px' : '10px'};
    }
    
    ${breakpoint_small} {
        font-size: ${props => props.small ? '11px' : '13px'};
    }
`;

export const TileSubtitle = styled.p`
    font-size: 11px;
    color: ${color_shades_darker};
    margin: 0;
    font-weight: 500;
    
    ${breakpoint_small} {
        font-size: 10px;
    }
`;

// ==================== CHART CONTAINER STYLED COMPONENTS ====================

export const ChartContainer = styled.div`
    flex: 1;
    min-height: 0;
    position: relative;
    ${breakpoint_small} {
        height: 200px;
        min-height: 200px;
        flex: none;
    }
`;

// Specialized container for funnel charts with better mobile responsiveness
export const FunnelChartContainer = styled.div`
    flex: 1;
    min-height: 0;
    position: relative;
    ${breakpoint_small} {
        height: 280px;
        min-height: 280px;
        flex: none;
    }
`;

// Specialized container for tall charts (like Transfers) with more vertical space on mobile
export const TallChartContainer = styled.div`
    flex: 1;
    min-height: 0;
    position: relative;
    overflow: hidden;
    ${breakpoint_small} {
        height: 320px;
        min-height: 320px;
        flex: none;
    }
`;

export const ChartLoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${overlay_white};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 8px;
`;

export const EmptyStateMessage = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${color_shades_dark};
    font-size: 13px;
    text-align: center;
    padding: 24px;
    z-index: 15;
    background: ${color_defaults_white};
    
    ${breakpoint_small} {
        font-size: 11px;
        padding: 16px;
        line-height: 1.4;
    }
`;

// ==================== NUMBER DISPLAY STYLED COMPONENTS ====================

export const NumberLoadingWrapper = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60px;
`;

export const NumberDisplay = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 0;
`;

export const NoSubtextNumberDisplay = styled(NumberDisplay)`
    justify-content: center;
`;

/**
 * Responsive number used in the Sales Summary small tile (two stacked dollar amounts)
 */
export const SalesSummaryNumber = styled.span`
    font-size: 18px;
    font-weight: 600;
    color: ${color_shades_darkest};
    line-height: 1.1;

    /* Mac 13" at More Space scaling */
    ${breakpoint_macbook} {
        font-size: 16px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        font-size: 13px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        font-size: 11px;
    }
`;

export const LargeNumber = styled.div`
    font-size: 36px;
    font-weight: 700;
    color: ${color_shades_darkest};
    line-height: 1;
    letter-spacing: -1px;

    /* Mac 13" at "More Space" scaling - modest reduction */
    ${breakpoint_macbook} {
        font-size: 30px;
    }

    /* Mac 13" at default/tight scaling - smaller screens */
    ${breakpoint_macbook_small} {
        font-size: 18px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 14px;
    }
    
    ${breakpoint_small} {
        font-size: 24px;
    }
`;

export const NumberUnit = styled.div`
    font-size: 11px;
    color: ${color_shades_darker};
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 600;

    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        font-size: 10px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        font-size: 9px;
        margin-top: 3px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 8px;
        margin-top: 2px;
    }

    ${breakpoint_small} {
        font-size: 10px;
        margin-top: 3px;
    }
`;

export const BigNumber = styled.div`
    font-size: 48px;
    font-weight: 700;
    color: ${color_shades_darkest};
    line-height: 1;
    letter-spacing: -1.5px;

    /* Mac 13" at "More Space" scaling - modest reduction */
    ${breakpoint_macbook} {
        font-size: 40px;
    }

    /* Mac 13" at default/tight scaling - smaller screens */
    ${breakpoint_macbook_small} {
        font-size: 20px;
        letter-spacing: -0.5px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 16px;
        letter-spacing: -0.25px;
    }
    
    ${breakpoint_small} {
        font-size: 30px;
    }
`;

export const NumberSubtext = styled.div`
    font-size: 12px;
    color: ${color_shades_dark};
    margin-top: 8px;
    text-align: center;
    max-width: 200px;
    line-height: 1.4;

    /* Mac 13" at default/tight scaling - smaller screens */
    ${breakpoint_macbook_small} {
        font-size: 9px;
        margin-top: 4px;
        max-width: 140px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 8px;
        margin-top: 3px;
        max-width: 120px;
    }
    
    ${breakpoint_small} {
        font-size: 10px;
        margin-top: 6px;
        max-width: 160px;
    }
`;

export const UnavailableDataDisplay = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 8px 12px;
    text-align: center;
`;

export const UnavailableDataIcon = styled.div`
    font-size: 24px;
    margin-bottom: 6px;
    opacity: 0.6;
`;

export const UnavailableDataText = styled.div`
    font-size: 13px;
    color: ${color_shades_dark};
    line-height: 1.4;
    max-width: 140px;
    
    ${breakpoint_small} {
        font-size: 11px;
        max-width: 120px;
    }
`;

// ==================== LOADER STYLED COMPONENTS ====================

export const LoadingText = styled.p`
    text-align: center;
    color: ${color_shades_darker};
    padding: 20px;
    font-size: 13px;
`;

export const CustomLoader = styled(SecondaryLoader)`
    .spinner > div {
        background: ${color_colors_ocean};
    }
`;

export const LoaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 200px;
`;

// ==================== PLACEHOLDER STYLED COMPONENTS ====================

export const PlaceholderContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: calc(100vh - 160px);
    background-color: ${color_defaults_white};
    border: ${style_border_default};
    border-radius: 10px;
    padding: 60px 40px;
    box-shadow: ${shadow_sm};
    text-align: center;

    /* Chromebook / smaller laptops (1024–1279px) and Mac small */
    ${breakpoint_large} {
        padding: 40px 24px;
    }

    ${breakpoint_macbook_small} {
        padding: 44px 28px;
    }
    
    ${breakpoint_small} {
        padding: 32px 20px;
        min-height: calc(100vh - 200px);
    }
`;

export const PlaceholderText = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0 0 16px 0;
    letter-spacing: -0.3px;
    max-width: 500px;
    line-height: 1.3;

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 20px;
        margin: 0 0 12px 0;
    }

    ${breakpoint_macbook_small} {
        font-size: 21px;
    }
    
    ${breakpoint_small} {
        font-size: 18px;
        margin: 0 0 12px 0;
        max-width: 100%;
    }
`;

export const PlaceholderSubtext = styled.p`
    font-size: 15px;
    color: ${color_shades_dark};
    margin: 0;
    max-width: 520px;
    line-height: 1.6;

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 13px;
        max-width: 440px;
        line-height: 1.5;
    }

    ${breakpoint_macbook_small} {
        font-size: 14px;
        max-width: 480px;
    }
    
    ${breakpoint_small} {
        font-size: 13px;
        max-width: 100%;
        line-height: 1.5;
    }
`;

// ==================== SUMMARY STYLED COMPONENTS ====================

export const LargeTile = styled.div`
    grid-column: span 2;
    background-color: ${color_defaults_white};
    border: ${style_border_default};
    border-radius: 10px;
    box-shadow: ${shadow_sm};
    padding: 20px;
    display: flex;
    flex-direction: column;
    min-height: 280px;
    ${breakpoint_small} {
        grid-column: span 1;
        min-height: 240px;
    }
`;

export const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    flex: 1;
    align-content: center;
`;

export const SummaryCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: ${voicify_spark_lightest};
    border-radius: 8px;
    border: 1px solid ${color_shades_lighter};
    
    ${breakpoint_small} {
        padding: 12px 8px;
    }
`;

export const SummaryValue = styled.div`
    font-size: 28px;
    font-weight: 700;
    color: ${color_shades_darkest};
    line-height: 1;
    letter-spacing: -0.5px;
    margin-bottom: 6px;
    
    ${breakpoint_small} {
        font-size: 22px;
        margin-bottom: 4px;
    }
`;

export const SummaryLabel = styled.div`
    font-size: 11px;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    text-align: center;
    
    ${breakpoint_small} {
        font-size: 9px;
        letter-spacing: 0.3px;
    }
`;

// ==================== CALL DATA MODAL STYLED COMPONENTS ====================

export const CallDataModalContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${color_defaults_white};
    border-radius: 8px;
    overflow: hidden;
`;

export const CallDataModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid ${color_shades_light};
    flex-shrink: 0;
    background: ${color_defaults_white};
    border-radius: 8px 8px 0 0;

    ${breakpoint_macbook_small} {
        padding: 12px 16px;
    }

    ${breakpoint_large} {
        padding: 10px 14px;
    }
    
    ${breakpoint_small} {
        padding: 14px 16px;
    }
`;

export const CallDataModalTitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const CallDataModalTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0;

    ${breakpoint_macbook_small} {
        font-size: 15px;
    }

    ${breakpoint_large} {
        font-size: 13px;
    }
    
    ${breakpoint_small} {
        font-size: 16px;
    }
`;

export const CallDataDateRange = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${color_shades_darker};

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 10px;
    }
    
    ${breakpoint_small} {
        font-size: 11px;
    }
`;

export const CallDataModalCloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    color: ${color_shades_dark};
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
        color: ${color_shades_darkest};
    }

    ${breakpoint_macbook_small} {
        font-size: 20px;
    }

    ${breakpoint_large} {
        font-size: 18px;
    }
`;

export const CallDataScrollContainer = styled.div`
    flex: 1;
    padding: 0 24px 16px 24px;
    overflow-y: auto;
    overflow-x: auto;
    min-height: 0;
    position: relative;

    ${breakpoint_macbook_small} {
        padding: 0 14px 10px 14px;
    }

    ${breakpoint_large} {
        padding: 0 10px 8px 10px;
    }
    
    ${breakpoint_small} {
        padding: 0 12px 12px 12px;
    }
`;

export const CallDataTableEmptyOverlay = styled.div`
    position: absolute;
    top: 60px;
    left: 24px;
    right: 24px;
    padding: 40px;
    text-align: center;
    color: ${color_shades_dark};
    pointer-events: none;
    
    ${breakpoint_small} {
        left: 12px;
        right: 12px;
    }
`;

export const CallDataTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

export const CallDataTh = styled.th`
    text-align: left;
    padding: 12px 16px;
    background: ${voicify_spark_lightest};
    font-size: 13px;
    font-weight: 600;
    color: ${color_shades_darker};
    border-bottom: 2px solid ${color_shades_light};
    position: sticky;
    top: 0;
    z-index: 1;
    white-space: nowrap;

    ${breakpoint_macbook_small} {
        padding: 8px 8px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 6px 6px;
        font-size: 10px;
    }
    
    ${breakpoint_small} {
        padding: 10px 8px;
        font-size: 11px;
    }
`;

export const CallDataRow = styled.tr`
    &:nth-of-type(even) {
        background: ${color_shades_lighter};
    }

    &:hover {
        background: ${voicify_spark_lightest};
    }
`;

export const CallDataTd = styled.td`
    padding: 12px 16px;
    font-size: 13px;
    color: ${color_shades_darkest};
    border-bottom: 1px solid ${color_shades_light};
    vertical-align: middle;
    white-space: nowrap;

    &:last-child {
        white-space: normal;
    }

    ${breakpoint_macbook_small} {
        padding: 8px 8px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 6px 6px;
        font-size: 10px;
    }
    
    ${breakpoint_small} {
        padding: 10px 8px;
        font-size: 11px;
    }
`;

export const TranscriptLink = styled.a`
    color: ${voicify_spark_darker};
    text-decoration: none;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
        color: ${voicify_spark_darkest};
    }
`;

export const TruncatedLocationText = styled.span`
    display: block;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: default;

    &:hover {
        position: relative;
    }

    ${breakpoint_macbook_small} {
        max-width: 140px;
    }

    ${breakpoint_large} {
        max-width: 110px;
    }
    
    ${breakpoint_small} {
        max-width: 100px;
    }
`;

export const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    ${breakpoint_macbook_small} {
        gap: 4px;
    }

    ${breakpoint_large} {
        gap: 3px;
    }
    
    ${breakpoint_small} {
        gap: 4px;
    }
`;

export const CallTag = styled.span`
    display: inline-block;
    padding: 4px 10px;
    background: ${voicify_spark_lightest};
    color: ${voicify_spark_darker};
    border: 1px solid ${voicify_spark_light};
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;

    ${breakpoint_macbook_small} {
        padding: 3px 8px;
        font-size: 10px;
        border-radius: 10px;
    }

    ${breakpoint_large} {
        padding: 2px 6px;
        font-size: 9px;
        border-radius: 8px;
    }
    
    ${breakpoint_small} {
        padding: 3px 6px;
        font-size: 9px;
        border-radius: 8px;
    }
`;

export const CallDataModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-top: 1px solid ${color_shades_light};
    flex-shrink: 0;
    background: ${color_defaults_white};
    border-radius: 0 0 8px 8px;
    flex-wrap: wrap;
    gap: 12px;

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
        gap: 8px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
        gap: 6px;
    }
    
    ${breakpoint_small} {
        padding: 12px 16px;
        flex-direction: column;
        align-items: stretch;
    }
`;

export const ExportInfoText = styled.span`
    font-size: 13px;
    color: ${color_shades_darker};

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 10px;
    }
    
    ${breakpoint_small} {
        font-size: 11px;
        text-align: center;
    }
`;

export const ExportButton = styled.button`
    padding: 10px 24px;
    background: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover:not(:disabled) {
        background: ${voicify_spark_dark};
        box-shadow: ${shadow_md_alt};
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    ${breakpoint_macbook_small} {
        padding: 7px 14px;
        font-size: 12px;
    }

    ${breakpoint_large} {
        padding: 6px 12px;
        font-size: 11px;
    }
    
    ${breakpoint_small} {
        padding: 10px 20px;
        font-size: 13px;
        width: 100%;
    }
`;

export const ButtonSpinner = styled.span`
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

// ==================== FILTER CONTROLS STYLED COMPONENTS ====================

export const FilterControlsContainer = styled.div`
    padding: 16px 24px;
    border-bottom: 1px solid ${color_shades_light};
    background: ${color_shades_lighter};

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
    }
    
    ${breakpoint_small} {
        padding: 12px 16px;
    }
`;

export const FilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    
    ${breakpoint_small} {
        gap: 8px;
    }
`;

export const SearchInputWrapper = styled.div`
    display: flex;
    align-items: center;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 8px 12px;
    flex: 1;
    min-width: 250px;
    max-width: 350px;

    &:focus-within {
        border-color: ${voicify_spark_medium};
        box-shadow: 0 0 0 2px ${voicify_spark_lightest};
    }

    ${breakpoint_macbook_small} {
        padding: 6px 8px;
        min-width: 180px;
        max-width: 260px;
    }

    ${breakpoint_large} {
        padding: 5px 7px;
        min-width: 150px;
        max-width: 220px;
    }
    
    ${breakpoint_small} {
        min-width: 100%;
        max-width: 100%;
        padding: 8px 10px;
    }
`;

export const FilterSearchInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    font-size: 13px;
    color: ${color_shades_darkest};
    background: transparent;
    
    &::placeholder {
        color: ${color_shades_dark};
    }

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 11px;
    }
    
    ${breakpoint_small} {
        font-size: 14px; /* Prevents zoom on iOS */
    }
`;

export const ClearSearchButton = styled.button`
    background: none;
    border: none;
    color: ${color_shades_dark};
    font-size: 18px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    
    &:hover {
        color: ${color_shades_darkest};
    }
`;

export const FilterDropdownWrapper = styled.div`
    position: relative;
`;

export const FilterDropdownButton = styled.button<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: ${props => props.isActive ? voicify_spark_lightest : color_defaults_white};
    border: 1px solid ${props => props.isActive ? voicify_spark_medium : color_shades_light};
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.isActive ? voicify_spark_darker : color_shades_darkest};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${voicify_spark_lightest};
        border-color: ${voicify_spark_light};
    }

    ${breakpoint_macbook_small} {
        padding: 6px 10px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 5px 8px;
        font-size: 10px;
    }
`;

export const FilterBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: ${voicify_spark_darker};
    color: ${color_defaults_white};
    border-radius: 9px;
    font-size: 11px;
    font-weight: 600;
`;

export const DropdownArrow = styled.span`
    font-size: 8px;
    opacity: 0.6;
`;

export const FilterDropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 4px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    box-shadow: ${shadow_modal};
    min-width: 220px;
    max-height: 320px;
    overflow-y: auto;
    z-index: 100;
`;

export const FilterDropdownHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    border-bottom: 1px solid ${color_shades_light};
    font-size: 12px;
    font-weight: 600;
    color: ${color_shades_darker};
`;

export const ClearFilterLink = styled.button`
    background: none;
    border: none;
    color: ${voicify_spark_darker};
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    
    &:hover {
        text-decoration: underline;
    }
`;

export const FilterActionsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const FilterCheckboxItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    font-size: 13px;
    color: ${color_shades_darkest};
    transition: background 0.15s;

    &:hover {
        background: ${color_shades_lighter};
    }

    ${breakpoint_macbook_small} {
        padding: 7px 10px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 6px 8px;
        font-size: 10px;
    }
`;

export const FilterCheckbox = styled.div<{ checked: boolean }>`
    width: 16px;
    height: 16px;
    border: 2px solid ${props => props.checked ? voicify_spark_darker : color_shades_dark};
    border-radius: 3px;
    background: ${props => props.checked ? voicify_spark_darker : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;

    &::after {
        content: '${props => props.checked ? '✓' : ''}';
        color: ${color_defaults_white};
        font-size: 10px;
        font-weight: bold;
    }
`;

export const ClearAllFiltersButton = styled.button`
    padding: 8px 14px;
    background: transparent;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    color: ${color_shades_darker};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${color_shades_lighter};
        border-color: ${color_shades_darker};
    }
`;

export const ResultsInfo = styled.div`
    margin-top: 12px;
    font-size: 13px;
    color: ${color_shades_darker};

    ${breakpoint_macbook_small} {
        font-size: 11px;
        margin-top: 6px;
    }

    ${breakpoint_large} {
        font-size: 10px;
        margin-top: 4px;
    }
    
    ${breakpoint_small} {
        font-size: 11px;
        margin-top: 8px;
    }
`;

export const SortableCallDataTh = styled(CallDataTh) <{ isSorted?: boolean }>`
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    background: ${props => props.isSorted ? voicify_spark_light : voicify_spark_lightest};
    
    &:hover {
        background: ${voicify_spark_light};
    }
`;

export const CategoryBadge = styled.span<{ category: string }>`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    background: ${props => {
        switch (props.category) {
            case 'ACTION_ATTEMPTED':
                return chart_planned_fill_light;
            case 'IMMEDIATE_TRANSFER':
                return status_error_bg;
            case 'QUESTION_ANSWERED':
                return status_success_bg;
            case 'NO_INTERACTION':
                return status_neutral_bg;
            default:
                return status_neutral_bg;
        }
    }};
    color: ${props => {
        switch (props.category) {
            case 'ACTION_ATTEMPTED':
                return chart_planned_solid;
            case 'IMMEDIATE_TRANSFER':
                return status_error;
            case 'QUESTION_ANSWERED':
                return status_success;
            case 'NO_INTERACTION':
                return status_neutral;
            default:
                return status_neutral;
        }
    }};
    
    ${breakpoint_macbook_small} {
        padding: 3px 8px;
        font-size: 10px;
        border-radius: 10px;
    }

    ${breakpoint_large} {
        padding: 2px 6px;
        font-size: 9px;
        border-radius: 8px;
    }

    ${breakpoint_small} {
        padding: 3px 6px;
        font-size: 9px;
        border-radius: 8px;
    }
`;

// ==================== TRANSFER TYPE FILTER STYLED COMPONENTS ====================

export const TransferTypeFilterWrapper = styled.div`
    display: flex;
    gap: 8px;
    
    ${breakpoint_small} {
        gap: 4px;
        flex-wrap: wrap;
    }
`;

export const TransferTypeButton = styled.button<{ isActive: boolean; buttonType: 'all' | 'planned' | 'unplanned' }>`
    padding: 8px 14px;
    background: ${props => props.isActive
        ? (props.buttonType === 'planned' ? chart_planned_fill_light
            : props.buttonType === 'unplanned' ? chart_unplanned_fill_light
                : voicify_spark_lightest)
        : color_defaults_white};
    border: 1px solid ${props => props.isActive
        ? (props.buttonType === 'planned' ? chart_planned_solid
            : props.buttonType === 'unplanned' ? chart_unplanned_solid
                : voicify_spark_medium)
        : color_shades_light};
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.isActive
        ? (props.buttonType === 'planned' ? chart_planned_solid
            : props.buttonType === 'unplanned' ? chart_unplanned_solid
                : voicify_spark_darker)
        : color_shades_darkest};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${props =>
        props.buttonType === 'planned' ? chart_planned_fill_light
            : props.buttonType === 'unplanned' ? chart_unplanned_fill_light
                : voicify_spark_lightest};
    }
    
    ${breakpoint_macbook_small} {
        padding: 6px 10px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 5px 8px;
        font-size: 10px;
    }

    ${breakpoint_small} {
        padding: 6px 10px;
        font-size: 11px;
    }
`;

// ==================== COLUMN FILTER STYLED COMPONENTS ====================

export const FilterableColumnHeader = styled.th<{ hasActiveFilter?: boolean }>`
    background: ${props => props.hasActiveFilter ? voicify_spark_light : voicify_spark_lightest};
    color: ${color_shades_darker};
    font-weight: 600;
    font-size: 13px;
    padding: 14px 16px;
    text-align: left;
    border-bottom: 2px solid ${voicify_spark_light};
    position: sticky;
    top: 0;
    z-index: 8;
    white-space: nowrap;
    user-select: none;

    ${breakpoint_macbook_small} {
        padding: 8px 8px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 6px 6px;
        font-size: 10px;
    }
    
    ${breakpoint_small} {
        padding: 10px 8px;
        font-size: 11px;
    }
`;

// Sticky left column header for Location column in By Location table
export const StickyLocationHeader = styled(FilterableColumnHeader)`
    position: sticky;
    left: 0;
    z-index: 10;
    background: ${props => props.hasActiveFilter ? voicify_spark_light : voicify_spark_lightest};
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
    min-width: 180px;
`;

// Sticky left column cell for Location column in By Location table
export const StickyLocationCell = styled(CallDataTd)`
    position: sticky;
    left: 0;
    z-index: 1;
    background: ${color_defaults_white};
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
    min-width: 180px;
    
    tr:nth-of-type(even) & {
        background: ${color_shades_lighter};
    }
    
    tr:hover & {
        background: ${voicify_spark_lightest};
    }
`;

export const ColumnHeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ColumnHeaderText = styled.span`
    flex: 1;
`;

export const ColumnFilterIcon = styled.button<{ isActive?: boolean; isOpen?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    background: ${props => props.isActive ? voicify_spark_darker : 'transparent'};
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: ${props => props.isActive ? voicify_spark_darker : color_shades_lighter};
    }

    img {
        width: 12px;
        height: 12px;
        opacity: 0.6;
        filter: ${props => props.isActive ? 'brightness(0) invert(1)' : 'none'};
    }
`;

export const SortIcon = styled.button<{ isActive?: boolean; direction?: 'asc' | 'desc' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    background: ${props => props.isActive ? voicify_spark_darker : 'transparent'};
    border: none;
    border-radius: 4px;
    color: ${props => props.isActive ? color_defaults_white : color_shades_dark};
    cursor: pointer;
    transition: all 0.15s;
    font-size: 10px;

    &:hover {
        background: ${props => props.isActive ? voicify_spark_darker : color_shades_lighter};
    }
`;

export const ColumnFilterDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 240px;
    max-width: 320px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    box-shadow: ${shadow_xl};
    z-index: 100;
    margin-top: 4px;
`;

export const ColumnFilterHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid ${color_shades_light};
    font-size: 12px;
    font-weight: 600;
    color: ${color_shades_darker};
`;

export const ColumnFilterContent = styled.div`
    max-height: 280px;
    overflow-y: auto;
`;

export const ColumnFilterItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;
    font-size: 13px;
    color: ${color_shades_darkest};
    transition: background 0.15s;

    &:hover {
        background: ${color_shades_lighter};
    }

    ${breakpoint_macbook_small} {
        padding: 7px 10px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 6px 8px;
        font-size: 10px;
    }
`;

export const ColumnFilterCheckbox = styled.div<{ state: 'unchecked' | 'checked' | 'excluded' }>`
    width: 16px;
    height: 16px;
    border: 2px solid ${props =>
        props.state === 'checked' ? voicify_spark_darker :
            props.state === 'excluded' ? status_error :
                color_shades_dark};
    border-radius: 3px;
    background: ${props =>
        props.state === 'checked' ? voicify_spark_darker :
            props.state === 'excluded' ? status_error :
                'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;

    &::after {
        content: '${props =>
        props.state === 'checked' ? '✓' :
            props.state === 'excluded' ? '×' :
                ''}';
        color: ${color_defaults_white};
        font-size: 10px;
        font-weight: bold;
    }
`;

// ==================== PHONE FILTER STYLED COMPONENTS ====================

export const PhoneFilterSection = styled.div`
    padding: 8px 12px;
    border-bottom: 1px solid ${color_shades_lighter};
`;

export const PhoneFilterModeToggle = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
`;

export const PhoneFilterModeButton = styled.button<{ isActive: boolean; mode: 'include' | 'exclude' }>`
    flex: 1;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 500;
    border: 1px solid ${props => props.isActive
        ? (props.mode === 'exclude' ? status_error : voicify_spark_darker)
        : color_shades_light};
    border-radius: 6px;
    background: ${props => props.isActive
        ? (props.mode === 'exclude' ? status_error_bg_light : voicify_spark_lightest)
        : 'transparent'};
    color: ${props => props.isActive
        ? (props.mode === 'exclude' ? status_error : voicify_spark_darker)
        : color_shades_darker};
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: ${props => props.mode === 'exclude' ? status_error_bg_xlight : color_shades_lighter};
    }
`;

export const TagFilterModeToggle = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
    padding: 0 12px;
`;

export const TagFilterModeButton = styled.button<{ isActive: boolean }>`
    flex: 1;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 500;
    border: 1px solid ${props => props.isActive ? voicify_spark_darker : color_shades_light};
    border-radius: 6px;
    background: ${props => props.isActive ? voicify_spark_lightest : 'transparent'};
    color: ${props => props.isActive ? voicify_spark_darker : color_shades_darker};
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: ${color_shades_lighter};
    }
`;

export const PhoneFilterInput = styled.input`
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
    font-size: 13px;
    margin-bottom: 8px;

    &:focus {
        outline: none;
        border-color: ${voicify_spark_medium};
    }

    &::placeholder {
        color: ${color_shades_dark};
    }
`;

export const PhoneFilterList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

export const PhoneFilterTag = styled.div<{ isExclude?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.isExclude ? status_error_bg_light : voicify_spark_lightest};
    border: 1px solid ${props => props.isExclude ? status_error : voicify_spark_light};
    border-radius: 4px;
    font-size: 12px;
    color: ${props => props.isExclude ? status_error : voicify_spark_darker};
`;

export const PhoneFilterTagRemove = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 12px;
    opacity: 0.7;

    &:hover {
        opacity: 1;
    }
`;

export const FilterSearchInColumn = styled.input`
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-bottom: 1px solid ${color_shades_light};
    font-size: 13px;

    &:focus {
        outline: none;
        border-bottom-color: ${voicify_spark_medium};
    }

    &::placeholder {
        color: ${color_shades_dark};
    }
`;

export const SimpleFilterControlsContainer = styled.div`
    padding: 16px 24px;
    border-bottom: 1px solid ${color_shades_lighter};
    flex-shrink: 0;
    background: ${color_defaults_white};

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
    }
    
    ${breakpoint_small} {
        padding: 12px 16px;
    }
`;

export const SimpleFilterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    ${breakpoint_macbook_small} {
        gap: 8px;
    }

    ${breakpoint_large} {
        gap: 6px;
    }
    
    ${breakpoint_small} {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`;

// ==================== BUTTON STYLE HELPERS ====================

export const viewExportButtonStyle = {
    padding: '6px 14px',
    background: button_dark_bg,
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s ease',
};

export const viewExportButtonHoverStyle = {
    background: button_dark_bg_hover,
    boxShadow: button_shadow_light,
};

// ==================== MOBILE-RESPONSIVE DASHBOARD COMPONENTS ====================

/**
 * Responsive "View Details" button - pill-shaped button used in tile headers
 */
export const ViewDetailsButton = styled.button<{ disabled?: boolean }>`
    padding: 5px 12px;
    background: ${button_dark_bg};
    color: white;
    border: none;
    border-radius: 16px;
    font-size: 10px;
    font-weight: 600;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    white-space: nowrap;
    transition: all 0.2s ease;
    opacity: ${props => props.disabled ? 0.5 : 1};

    &:hover:not(:disabled) {
        background: ${button_dark_bg_hover};
        box-shadow: ${button_shadow_light};
    }

    /* Mac 13" at More Space scaling */
    ${breakpoint_macbook} {
        padding: 5px 10px;
        font-size: 10px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        padding: 4px 9px;
        font-size: 9px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        padding: 3px 7px;
        font-size: 8px;
    }

    ${breakpoint_small} {
        padding: 5px 10px;
        font-size: 10px;
    }
`;

/**
 * Wrapper for button(s) in tile headers with responsive gap
 */
export const TileHeaderButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;

    /* Mac 13" at More Space scaling */
    ${breakpoint_macbook} {
        gap: 10px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        gap: 8px;
    }

    /* Chromebook / smaller laptops */
    ${breakpoint_large} {
        gap: 6px;
    }

    ${breakpoint_small} {
        gap: 8px;
    }
`;

/**
 * Container for overlay stats positioned on charts
 */
export const ChartOverlayContainer = styled.div<{ top?: string; right?: string }>`
    position: absolute;
    top: ${props => props.top || '-32px'};
    right: ${props => props.right || '8px'};
    text-align: right;
    z-index: 10;
    display: flex;
    align-items: flex-start;
    gap: 24px;

    ${breakpoint_small} {
        top: -24px;
        right: 4px;
        gap: 12px;
    }
`;

/**
 * Single stat block within a chart overlay
 */
export const ChartOverlayStatBlock = styled.div`
    text-align: right;
`;

/**
 * Large number display in chart overlays (32px → scaled on smaller screens → 24px on mobile)
 */
export const ChartOverlayNumber = styled.div`
    font-size: 32px;
    font-weight: 700;
    color: ${color_shades_darkest};
    line-height: 1;
    letter-spacing: -1px;

    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        font-size: 26px;
        letter-spacing: -0.5px;
    }

    /* Mac 13" at default/tight scaling - smaller screens */
    ${breakpoint_macbook_small} {
        font-size: 17px;
        letter-spacing: -0.25px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 14px;
        letter-spacing: 0;
    }

    ${breakpoint_small} {
        font-size: 24px;
        letter-spacing: -0.5px;
    }
`;

/**
 * Label text under chart overlay numbers
 */
export const ChartOverlayLabel = styled.div`
    font-size: 11px;
    font-weight: 500;
    color: ${color_shades_dark};
    margin-top: 4px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;

    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        font-size: 10px;
    }

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        font-size: 9px;
        margin-top: 3px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 8px;
        margin-top: 2px;
    }

    ${breakpoint_small} {
        font-size: 9px;
        margin-top: 2px;
    }
`;

/**
 * Container for stats displayed in tile headers (e.g., transfer totals)
 */
export const TileHeaderStatsSection = styled.div`
    text-align: right;
`;

/**
 * Number display in tile headers (24px → scaled on smaller screens → 18px on mobile)
 */
export const TileHeaderNumber = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${color_shades_darkest};
    line-height: 1;
    letter-spacing: -0.5px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;

    /* Mac 13" at "More Space" scaling */
    ${breakpoint_macbook} {
        font-size: 20px;
        letter-spacing: -0.25px;
    }

    /* Mac 13" at default/tight scaling - smaller screens */
    ${breakpoint_macbook_small} {
        font-size: 14px;
        letter-spacing: -0.25px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 11px;
        letter-spacing: 0;
    }

    ${breakpoint_small} {
        font-size: 18px;
        letter-spacing: -0.25px;
        gap: 2px;
    }
`;

/**
 * Label text under tile header numbers
 */
export const TileHeaderLabel = styled.div`
    font-size: 11px;
    font-weight: 500;
    color: ${color_shades_dark};
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;

    /* Mac 13" at default/tight scaling */
    ${breakpoint_macbook_small} {
        font-size: 9px;
    }

    /* Chromebook / smaller laptops (1024–1279px) */
    ${breakpoint_large} {
        font-size: 8px;
    }

    ${breakpoint_small} {
        font-size: 9px;
        margin-top: 1px;
    }
`;

/**
 * Modal Tab Navigation - container for tab buttons
 */
export const ModalTabNavigation = styled.div`
    display: flex;
    gap: 0;
    padding: 0 32px;
    background: ${color_defaults_white};
    border-bottom: 1px solid ${color_shades_light};
    flex-shrink: 0;
`;

/**
 * Modal Tab Button - individual tab within modal
 */
export const ModalTabButton = styled.button<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 24px;
    background: none;
    border: none;
    border-bottom: 3px solid ${props => props.isActive ? voicify_spark_medium : 'transparent'};
    font-size: 14px;
    font-weight: ${props => props.isActive ? 600 : 500};
    color: ${props => props.isActive ? voicify_spark_darkest : color_shades_darker};
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -1px;

    &:hover {
        color: ${voicify_spark_darkest};
        background: rgba(14, 133, 187, 0.04);
    }

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
        font-size: 12px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
        font-size: 11px;
    }
`;

/**
 * Modal Tab Content - container for content of each tab
 */
export const ModalTabContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

/**
 * By Location Tab placeholder content
 */
export const ByLocationPlaceholder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 60px 40px;
    color: ${color_shades_dark};
    font-size: 14px;
    text-align: center;
`;
